"use client";
import type { ReactNode, Ref } from "react";
import React, { forwardRef, useRef, useState } from "react";
import { Combobox, ComboboxInput, Label as HLabel } from "@headlessui/react";
import type { Nil } from "cy-util";
import type { FieldState, FormControlSize } from "../models";
import { useFormGroup } from "../FormGroup";
import { useComposedRefs } from "../../../hooks";
import { Label } from "../Label";
import { formControlVariants } from "../classes";
import { SelectOptions } from "./SelectOptions";
import { SelectNothingFound } from "./SelectNothingFound";
import { SelectTransition } from "./SelectTransition";
import { SelectButton } from "./SelectButton";
import { cn } from "../../../utils/cn";

// https://fettblog.eu/typescript-react-generic-forward-refs/
// Redecalare forwardRef
// declare module "react" {
//   function forwardRef<T, P = {}>(
//     render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
//   ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
// }

export interface RenderOptionProps<TItem> {
  item: TItem;
}

interface RefProp {
  ref?: Ref<HTMLInputElement>;
}
export interface SelectAutoCompleteProps<TItem> extends RefProp {
  className?: string;
  sizeVariant?: FormControlSize;
  /**
   * Can the value be cleared
   */
  nullable?: boolean;
  items: TItem[] | Nil;
  value: TItem | Nil;
  displayValue?: (item: TItem) => string;
  onChange: (item: TItem | Nil) => void;
  onBlur?: () => void;
  onQueryChange?: (query: string) => void;
  fieldState?: FieldState;
  name: string;
  label?: string;
  children: (props: RenderOptionProps<TItem>) => ReactNode;
  disabled?: boolean;
  id?: string;
  required?: boolean;
  "data-testid"?: string;
}
function SelectAutoCompleteRoot<TItem>(
  {
    className,
    items,
    value,
    name,
    nullable,
    displayValue,
    onChange,
    onQueryChange,
    onBlur,
    label,
    fieldState,
    required,
    children,
    disabled,
    sizeVariant = "md",
    "data-testid": dataTestId,
  }: SelectAutoCompleteProps<TItem>,
  forwardedRef: Ref<HTMLInputElement>,
) {
  const formGroup = useFormGroup();
  fieldState = fieldState || formGroup?.controller?.fieldState;
  const error = fieldState?.error;
  const [query, setQuery] = useState("");

  displayValue = displayValue ?? ((x: any) => (x == null ? "" : `${x}`));
  const inputRef = useRef<any>(null);
  const ref = useComposedRefs(forwardedRef, inputRef);

  function queryChange(newQuery: string) {
    setQuery(newQuery);
    onQueryChange && onQueryChange(newQuery);
  }

  function valueChanged(newValue: TItem | null) {
    onChange && onChange(newValue);
  }

  function onFocus() {
    inputRef.current?.select();
  }

  // Auto-generate data-testid from name if not provided
  const generateTestId = () => {
    if (dataTestId) return dataTestId;
    if (name) return `select-autocomplete-${name}`;
    if (formGroup?.id) return `select-autocomplete-${formGroup.id}`;
    return "select-autocomplete";
  };

  const testId = generateTestId();

  // TODO: Need to come back to this
  return (
    <>
      {items ? (
        <Combobox
          disabled={disabled}
          nullable={nullable as any}
          onChange={valueChanged as any}
          value={value as any}
        >
          {() => (
            <div className="" data-testid={testId}>
              {label ? (
                <HLabel as={Label} required={required}>
                  {label}
                </HLabel>
              ) : null}
              <div className="relative">
                <ComboboxInput
                  autoComplete="off"
                  className={cn(
                    "input w-full px-3 !pr-8 pr-4",
                    formControlVariants({
                      vSize: sizeVariant,
                      vColor: error ? "error" : undefined,
                    }),
                    className,
                  )}
                  displayValue={displayValue}
                  name={name}
                  onBlur={onBlur}
                  onChange={(event) => queryChange(event.target.value)}
                  onFocus={onFocus}
                  ref={ref}
                  data-testid="select-autocomplete-input"
                />
                <SelectButton />
              </div>

              <SelectTransition afterLeave={() => queryChange("")}>
                <SelectOptions>
                  {items.length === 0 && query !== "" ? (
                    <SelectNothingFound />
                  ) : (
                    items.map((item, index) => (
                      <div
                        key={index}
                        data-testid={`select-autocomplete-option-${index}`}
                      >
                        {children({ item })}
                      </div>
                    ))
                  )}
                </SelectOptions>
              </SelectTransition>
            </div>
          )}
        </Combobox>
      ) : null}
    </>
  );
}
interface WithForwardRefType extends React.FC<SelectAutoCompleteProps<any>> {
  <T>(
    props: SelectAutoCompleteProps<T>,
  ): ReturnType<React.FC<SelectAutoCompleteProps<T>>>;
}
export const SelectAutoComplete: WithForwardRefType = forwardRef(
  SelectAutoCompleteRoot,
);
